import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon, icons } from 'smartsuite-ui';
import { getLegendFormatter } from '../viewDashboards/widgets/utils/dataFormatUtils';

import { getChartRenderProps } from '../viewDashboards/dashboardUtils';
import DashboardContext from '../shared/hooks/dashboardContext';

import { getThresholdsWithValues } from '../viewDashboards/utils/configurationAndValidationUtils';
import { buildWidgetMetricRequests } from '../viewDashboards/utils/metricRequestUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import TextOrLinkTitle from '../shared/TextOrLinkTitle';
import {
    buildWidgetFilterState,
    getNavigationLink,
} from '../viewDashboards/dashboardLinkUtils';
import WidgetLoading from '../shared/WidgetLoading';

function getWidgetClass(hasChart = true) {
    let className = 'custom-dashboard-widget widget';

    if (hasChart) {
        className += ' widget--with-chart';
    }

    return className;
}

export default function CustomDashboardWidget(props) {
    const { widgetConfiguration } = props;

    const [uri, setUri] = useState(null);
    const { uriConstants } = useContext(DashboardContext);
    const { dashboardLabels, predicateLabels } = useLabelContext();

    const widgetFilterState = buildWidgetFilterState(
        props.defaultTimeAndPrecision,
        props.defaultFilterValues,
        props.defaultWhereClauses,
        widgetConfiguration
    );

    useEffect(() => {
        let isMounted = true;

        async function getUri() {
            const navigationUri = await getNavigationLink(
                widgetConfiguration.navigationLink,
                uriConstants,
                [widgetFilterState],
                predicateLabels
            );

            if (isMounted) {
                setUri(navigationUri);
            }
        }

        if (widgetConfiguration.navigationLink) {
            getUri();
        }

        return () => {
            isMounted = false;
        };
    }, [
        predicateLabels,
        uriConstants,
        widgetConfiguration.navigationLink,
        widgetFilterState,
    ]);

    const legendFormatter = getLegendFormatter(
        widgetConfiguration.friendlyLegendLabels,
        props.friendlyColumnLabels
    );

    const className = getWidgetClass(
        widgetConfiguration.configuration.hasChart
    );

    const thresholdsWithValues = getThresholdsWithValues(
        widgetConfiguration.thresholds,
        props.thresholdValues,
        widgetFilterState.filterValues
    );

    function renderWidgetBody() {
        if (props.isLoading) {
            return (
                <div
                    className="widget-body--loading"
                    style={widgetConfiguration.bodyStyles}
                >
                    <WidgetLoading noOverlayBackground={true} />
                </div>
            );
        }

        return (
            <div className="widget-body" style={widgetConfiguration.bodyStyles}>
                {widgetConfiguration.configuration.render(
                    getChartRenderProps(
                        widgetConfiguration,
                        buildWidgetMetricRequests(
                            widgetConfiguration,
                            props.defaultTimeAndPrecision,
                            props.defaultFilterValues,
                            props.defaultWhereClauses,
                            props.defaultRecordLimit,
                            false
                        ),
                        props.metrics,
                        thresholdsWithValues,
                        legendFormatter,
                        dashboardLabels,
                        props.openLinksInNewTab
                    )
                )}
            </div>
        );
    }

    return (
        <div className={className}>
            <div
                className="custom-dashboard-widget__header"
                style={widgetConfiguration.headerStyles}
            >
                <div className="title title--size-small widget__header-text">
                    <TextOrLinkTitle
                        navigationLink={uri}
                        openLinkInNewTab={props.openLinksInNewTab}
                        title={widgetConfiguration.title}
                    />
                </div>
                <ButtonWithIcon
                    iconClass={icons.edit}
                    onClick={() =>
                        props.navigateToEditor(widgetConfiguration.id)
                    }
                />
            </div>
            {renderWidgetBody()}
        </div>
    );
}

CustomDashboardWidget.propTypes = {
    defaultTimeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    defaultFilterValues: PropTypes.instanceOf(Object).isRequired,
    defaultWhereClauses: PropTypes.instanceOf(Array).isRequired,
    defaultRecordLimit: PropTypes.instanceOf(Object).isRequired,
    friendlyColumnLabels: PropTypes.instanceOf(Object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    navigateToEditor: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    thresholdValues: PropTypes.instanceOf(Array),
    widgetConfiguration: PropTypes.instanceOf(Object).isRequired,
};
